'use client';

import { CustomImage as Image } from '..';
import { forwardRef, useCallback, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useCartStore, useShallow } from '..';
import { useClickOutside } from 'hooks/dom/useClickOutside';
import { maxAsset } from 'content/products/max/assets';
import { getCurrencySymbolFromEnum } from 'hooks/pricing/useFormattedPrice';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { getTaxString } from 'components/shared/Cart/utils';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { Currency } from 'utils/Currency';
import { DotSpinner } from 'components/shared';
import {
  MAX_DEFAULT_PLAN,
  MaxPlanKey,
  buildMaxLineItem,
  getMaxConfig,
  getMaxPowerPlugCount,
} from 'content/products/max';
import { MaxPlanPicker } from 'components/shared/Cart/MaxPlanPicker';
import { useMaxPlanCopy } from 'components/shared/Cart/hooks/useMaxPlanCopy';
import {
  ShopProductCardRoot,
  ShopCardBody,
  ShopCardBottomSheet,
  ShopCardFooterRow,
  ShopCardMediaDimBackdrop,
  ShopCardPlanTileMedia,
  ShopCardPriceCluster,
  ShopCardPrimaryCta,
  ShopCardSpecSection,
  ShopCardTag,
} from './shopProductCard';
import styles from './ProductCardMax.module.css';

type ProductCardMaxProps = {
  className?: string;
};

/** /shop tile for the Ultrahuman MAX subscription: Monthly / Yearly chooser in the bottom sheet. */
const ProductCardMaxInner = forwardRef<HTMLDivElement, ProductCardMaxProps>(
  function ProductCardMaxInner({ className }, ref) {
    const t = useTranslations('cart');
    const CartClient = useCartStore(
      useShallow((s) => ({
        store: s.store,
        taxString: s.taxString,
        currency: s.currency,
        addToCart: s.addToCart,
      })),
    );
    const { store: regionStore, region } = useRegionLocale();
    const resolvedStore = (CartClient.store ?? regionStore) as ShopifyStore;
    const tCart = useTranslations('cart');
    const resolvedTaxString =
      CartClient.taxString || getTaxString(resolvedStore, tCart);
    const [isLoading, setIsLoading] = useState(false);
    const [showPlanSelect, setShowPlanSelect] = useState(false);
    const [plan, setPlan] = useState<MaxPlanKey>(MAX_DEFAULT_PLAN);
    const innerRef = useRef<HTMLDivElement>(null);
    const { priceLabel, entryPrice } = useMaxPlanCopy();
    const currencySymbol = getCurrencySymbolFromEnum(
      (CartClient.currency ?? Currency.usd) as Currency,
    );
    const config = getMaxConfig(resolvedStore);
    const entry = config ? entryPrice(config, currencySymbol) : null;

    const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    const closeSheet = useCallback(() => {
      setTimeout(() => setShowPlanSelect(false), 200);
    }, []);

    useClickOutside(innerRef, closeSheet, showPlanSelect);

    if (!config) return null;

    const handleAddToCart = async () => {
      const line = buildMaxLineItem(resolvedStore, plan, { funnel: 'shop' });
      if (!line) return;
      try {
        setIsLoading(true);
        await CartClient.addToCart([line]);
      } finally {
        setIsLoading(false);
        closeSheet();
      }
    };

    const title = t('sharedCart.max.name');

    return (
      <ShopProductCardRoot
        ref={setRefs}
        className={[styles.root, className].filter(Boolean).join(' ')}
      >
        <ShopCardPlanTileMedia onClick={() => setShowPlanSelect(false)}>
          <Image
            src={maxAsset('shop-tile-logo.png')}
            alt={title}
            height={1146}
            width={1065}
            style={{ objectFit: 'cover', width: '100%' }}
          />
        </ShopCardPlanTileMedia>
        <ShopCardBody>
          <ShopCardSpecSection>
            <h3 className="title">{title}</h3>
            <p className="description">
              {t('sharedCart.upsell.max.description', {
                count: getMaxPowerPlugCount(region),
              })}
            </p>
            <br />
          </ShopCardSpecSection>

          <ShopCardFooterRow>
            <ShopCardPriceCluster>
              <span style={{ flexBasis: '100%' }}>{entry?.prefix}</span>
              {entry?.price}
              <span>{resolvedTaxString}</span>
            </ShopCardPriceCluster>
            <ShopCardPrimaryCta
              className={isLoading ? 'quickAdd disable' : 'quickAdd'}
              onClick={() => setShowPlanSelect(true)}
              data-buttontype="add to cart"
              data-product={title}
              disabled={isLoading}
              aria-busy={isLoading}
              data-variantid={config.plans[plan].variantId}
            >
              {isLoading ? <DotSpinner color="#FFFFFF" /> : t('sharedCart.upsell.addToCart')}
            </ShopCardPrimaryCta>
          </ShopCardFooterRow>
        </ShopCardBody>
        <ShopCardTag>{t('sharedCart.addons.tags.popular')}</ShopCardTag>

        <ShopCardMediaDimBackdrop
          className={showPlanSelect ? 'open' : ''}
          onClick={closeSheet}
        />
        <ShopCardBottomSheet
          className={`sizeOverlay ${showPlanSelect ? 'open' : ''} ${styles.bottomSheet}`}
          aria-hidden={!showPlanSelect}
        >
          <div className={styles.planSelect}>
            <span className={styles.planTitle}>{t('sharedCart.max.chooseSubscription')}</span>
            <MaxPlanPicker
              config={config}
              currencySymbol={currencySymbol}
              value={plan}
              onChange={setPlan}
              analyticsPrefix="shop"
              disabled={isLoading}
            />
            <button
              type="button"
              className={`${styles.addToCart} ${isLoading ? styles.disabled : ''} gtm`}
              onClick={handleAddToCart}
              disabled={isLoading}
              data-buttontype="shop-add-max"
              data-variantid={config.plans[plan].variantId}
            >
              {isLoading ? (
                <DotSpinner color="#fff" />
              ) : (
                t('sharedCart.max.addToCartFor', {
                  price: priceLabel(config, plan, currencySymbol),
                })
              )}
            </button>
          </div>
        </ShopCardBottomSheet>
      </ShopProductCardRoot>
    );
  },
);

export const ProductCardMax = ProductCardMaxInner;
